import { Helmet } from "react-helmet-async";
import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import api from "../../Utils/Axios";

// Urdu font style
const UR = { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', Arial, sans-serif", direction: 'rtl' };

// Helper to get numeric value from expenses map
const getExpVal = (expenses, ...keys) => {
  if (!expenses) return 0;
  for (const key of keys) {
    const v = expenses[key];
    if (v !== undefined && v !== null && v !== '') {
      const n = parseFloat(v);
      if (!isNaN(n)) return n;
    }
  }
  return 0;
};

const fmt = (n) => {
  const num = parseFloat(n) || 0;
  if (num === 0) return '';
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const urduMonths = [
  "جنوری", "فروری", "مارچ", "اپریل", "مئی", "جون",
  "جولائی", "اگست", "ستمبر", "اکتوبر", "نومبر", "دسمبر"
];

// Calculate Net Sale (پختہ بکری) - same logic as BillsTab
const calculateNetSale = (bill) => {
  // Get raw sale (خام بکری)
  const rawSale = bill.sales?.rawSale || bill.sales?.['Raw Sale'] || bill.expenses?.['خام بکری'] || bill.expenses?.['خام بکری'] || bill.rawSale || 0;
  const rawSaleNum = typeof rawSale === 'string' ? parseFloat(rawSale) || 0 : (rawSale || 0);

  // Calculate total expenses (exclude non-expense fields and laga)
  let totalExpenses = 0;
  if (bill.expenses && typeof bill.expenses === 'object') {
    Object.keys(bill.expenses).forEach(key => {
      const value = bill.expenses[key];
      const isNonExpenseField =
        key === 'Vehicle' ||
        key === 'ActualVehicle' ||
        key === 'ItemName' ||
        key === 'ItemUnit' ||
        key === 'ItemQuantity' ||
        key === 'خام بکری' ||
        key === 'خام بکری' ||
        key === 'اخراجات' ||
        key === 'پختہ بکری' ||
        key === 'پختہ بِکری' ||
        key === 'لاگا' ||
        key === 'Laga' ||
        key === 'DeliveryMethod' ||
        key === 'GoodsMessageUrdu';

      if (!isNonExpenseField) {
        const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (typeof value === 'number' && !isNaN(value) ? value : 0);
        totalExpenses += numValue;
      }
    });
  }
  return rawSaleNum - totalExpenses;
};

const getTodayDate = () => new Date().toLocaleDateString('en-CA');

function RozNamachaPart2({ company, companyLanguage, navigate }) {

  const getFirstDayOfMonth = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA');
  };

  const [dateFrom, setDateFrom] = useState(getFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getTodayDate());

  const [selMonth, setSelMonth] = useState(new Date().getMonth());
  const [selYear, setSelYear] = useState(new Date().getFullYear());

  const dateFromRef = useRef(null);
  const dateToRef = useRef(null);
  const [loading, setLoading] = useState(false);

  // Handle Month/Year Change
  const updateRangeByMonthYear = (m, y) => {
    const firstDay = new Date(y, m, 1).toLocaleDateString('en-CA');
    const lastDay = new Date(y, m + 1, 0).toLocaleDateString('en-CA');
    setDateFrom(firstDay);
    setDateTo(lastDay);
  };

  const handleMonthYearChange = (m, y) => {
    setSelMonth(m);
    setSelYear(y);
    updateRangeByMonthYear(m, y);
  };

  // All raw data
  const [bills, setBills] = useState([]);
  const [sales, setSales] = useState([]);
  const [recoveries, setRecoveries] = useState([]);
  const [expenses, setExpenses] = useState([]);

  // Merged rows for table
  // Merged rows for table
  const [rows, setRows] = useState([]);
  const [expandedDates, setExpandedDates] = useState({}); // Tracking expanded date rows

  // Totals
  const [totals, setTotals] = useState({
    khabBikri: 0, bills: 0, sales: 0, recoveries: 0,
    lagan: 0, commission: 0, mashayana: 0, marketFee: 0, ikhrajat: 0
  });

  useEffect(() => {
    if (company) fetchAll();
    // eslint-disable-next-line
  }, [company, dateFrom, dateTo]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("companyToken");
      const h = { Authorization: `Bearer ${token}` };
      const params = { dateFrom, dateTo, limit: 1000 };

      const [billsRes, salesRes, recRes, expRes] = await Promise.all([
        api.get('/bill/company/my-bills', { headers: h, params }).catch(() => ({ data: {} })),
        api.get('/sale/company/all', { headers: h, params }).catch(() => ({ data: {} })),
        api.get('/recovery/company/all', { headers: h, params }).catch(() => ({ data: {} })),
        api.get('/expense-ledger/company/all', { headers: h, params }).catch(() => ({ data: {} })),
      ]);

      setBills(billsRes.data?.bills || []);
      setSales(salesRes.data?.sales || []);
      setRecoveries(recRes.data?.recoveries || []);
      setExpenses(expRes.data?.expenses || []);
    } catch (err) {
      toast.error("ڈیٹا لوڈ نہیں ہو سکا");
    } finally {
      setLoading(false);
    }
  };

  // Build merged rows whenever data or date filter changes
  useEffect(() => {
    buildRows();
    // eslint-disable-next-line
  }, [bills, sales, recoveries, expenses, dateFrom, dateTo]);

  const inRange = (dateStr) => {
    if (!dateStr) return false;
    const d = dateStr.substring(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };

  const buildRows = () => {
    // 1. Group all records by date
    const groups = {};

    const addToGroup = (dateStr, type, data) => {
      const d = dateStr ? new Date(dateStr).toLocaleDateString('en-CA') : 'No Date';
      if (!inRange(d)) return;
      if (!groups[d]) {
        groups[d] = {
          date: d,
          bills: [],
          sales: [],
          recoveries: [],
          expenses: [],
          totals: { khabBikri: 0, pukhtaBikri: 0, sales: 0, recoveries: 0, lagan: 0, commission: 0, mashayana: 0, marketFee: 0, ikhrajat: 0 }
        };
      }
      groups[d][type].push(data);
    };

    // Process Bills
    bills.forEach(b => {
      const d = b.date ? new Date(b.date).toLocaleDateString('en-CA') : '';
      if (!inRange(d)) return;

      const netS = calculateNetSale(b);
      const khab = b.sales?.rawSale || b.sales?.['Raw Sale'] || b.expenses?.['خام بکری'] || b.expenses?.['خام بِکری'] || parseFloat(b.rawSale) || 0;
      const exp = b.expenses || {};
      const sls = b.sales || {};

      const lagan = getExpVal(exp, 'Laga', 'لاگا', 'Lagan', 'لاگن', 'lagan', 'Lagan Expense');
      const comm = getExpVal(exp, 'Commission', 'کمیشن', 'commission');
      const mash = getExpVal(exp, 'Mashayana', 'مشیانہ', 'مشیانه', 'مشایانہ', 'مشیانہ.', 'مشیانه.', 'Mashiana', 'mashayana', 'Mashiyana') ||
        getExpVal(sls, 'Mashayana', 'مشیانہ', 'مشیانه', 'مشایانہ', 'Mashiana');
      const mFee = getExpVal(exp, 'Market Fee', 'Market Fees', 'مارکیٹ فیس', 'market fee', 'marketfee');
      const ikh = khab - netS;

      const billData = {
        id: b._id,
        serial: b.serialNumber || b._id?.slice(-6),
        supplier: b.customerNameUrdu || b.customerName,
        khab,
        pukhta: netS,
        lagan,
        comm,
        mash,
        mFee,
        ikh
      };

      addToGroup(d, 'bills', billData);

      if (groups[d]) {
        groups[d].totals.khabBikri += khab;
        groups[d].totals.pukhtaBikri += netS;
        groups[d].totals.lagan += lagan;
        groups[d].totals.commission += comm;
        groups[d].totals.mashayana += mash;
        groups[d].totals.marketFee += mFee;
        groups[d].totals.ikhrajat += ikh;
      }
    });

    // Process Sales
    sales.forEach(s => {
      const d = s.date ? new Date(s.date).toLocaleDateString('en-CA') : '';
      if (!inRange(d)) return;
      const amt = parseFloat(s.totalAmount || s.total) || 0;
      addToGroup(d, 'sales', { id: s._id, supplier: s.supplierNameUrdu || s.supplierName, amount: amt });
      if (groups[d]) groups[d].totals.sales += amt;
    });

    // Process Recoveries
    recoveries.forEach(r => {
      const d = r.date ? new Date(r.date).toLocaleDateString('en-CA') : '';
      if (!inRange(d)) return;
      const amt = r.recoveryAmount || r.amount || 0;
      addToGroup(d, 'recoveries', { id: r._id, party: r.partyNameUrdu || r.partyName, amount: amt });
      if (groups[d]) groups[d].totals.recoveries += amt;
    });

    // Process External Expenses
    expenses.forEach(e => {
      const d = e.date ? new Date(e.date).toLocaleDateString('en-CA') : '';
      if (!inRange(d)) return;
      const amt = e.amount || 0;
      addToGroup(d, 'expenses', { id: e._id, desc: e.description, amount: amt });
      if (groups[d]) groups[d].totals.ikhrajat += amt;
    });

    const sortedDates = Object.keys(groups).sort((a, b) => new Date(a) - new Date(b));
    const mergedRows = sortedDates.map((d, i) => ({
      sr: i + 1,
      ...groups[d]
    }));

    setRows(mergedRows);

    const t = { khabBikri: 0, bills: 0, sales: 0, recoveries: 0, lagan: 0, commission: 0, mashayana: 0, marketFee: 0, ikhrajat: 0, pukhtaBikri: 0 };
    mergedRows.forEach(r => {
      t.khabBikri += r.totals.khabBikri;
      t.pukhtaBikri += r.totals.pukhtaBikri;
      t.sales += r.totals.sales;
      t.recoveries += r.totals.recoveries;
      t.lagan += r.totals.lagan;
      t.commission += r.totals.commission;
      t.mashayana += r.totals.mashayana;
      t.marketFee += r.totals.marketFee;
      t.ikhrajat += r.totals.ikhrajat;
    });
    setTotals({ ...t, bills: mergedRows.length });
  };



  // --- Excel Export ---
  const handleExcel = () => {
    if (rows.length === 0) { toast.error("ایکسل کے لیے کوئی ڈیٹا نہیں"); return; }
    const data = [
      ['نمبر شمار', 'تاریخ', 'خام بکری', 'پختہ بکری', 'سیل', 'وصولی', 'لا گا', 'کمیشن', 'مشیانہ', 'مارکیٹ فیس', 'اخراجات'],
      ...rows.map(r => [r.sr, r.date, r.khabBikri || '', r.pukhtaBikri || '', r.sales || '', r.recoveries || '', r.lagan || '', r.commission || '', r.mashayana || '', r.marketFee || '', r.ikhrajat || '']),
      ['کل', '', fmt(totals.khabBikri), fmt(totals.pukhtaBikri), fmt(totals.sales), fmt(totals.recoveries), fmt(totals.lagan), fmt(totals.commission), fmt(totals.mashayana), fmt(totals.marketFee), fmt(totals.ikhrajat)],
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'روزنامچہ');
    XLSX.writeFile(wb, `Roznamacha_${dateFrom}_to_${dateTo}.xlsx`);
    toast.success("ایکسل ڈاؤن لوڈ ہو گئی");
  };


  // --- Print ---
  const handlePrint = () => {
    const w = window.open('', '_blank');
    const companyName = company?.shopName || company?.name || '';
    w.document.write(`<!DOCTYPE html><html dir="rtl"><head><title>روزنامچہ</title>
<style>
  body { font-family: 'Noto Nastaliq Urdu','Jameel Noori Nastaleeq',Arial,sans-serif; font-size: 11px; margin: 10px; direction: rtl; }
  h2 { text-align: center; margin: 4px 0; font-size: 14px; }
  p.sub { text-align: center; margin: 2px 0; color: #555; font-size: 11px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { background: #7a9e7e; color: white; padding: 4px 5px; border: 1px solid #999; font-size: 10px; text-align: center; }
  td { border: 1px solid #ccc; padding: 3px 5px; text-align: right; font-size: 10px; }
  td.left { text-align: left; }
  tr.total-row td { background: #f0f0f0; font-weight: bold; border-top: 2px solid #666; }
  .orange { color: #c75000; }
</style></head><body>
<h2>روزنامچہ (Day Book)</h2>
<p class="sub">${companyName} &nbsp;|&nbsp; ${dateFrom} to ${dateTo}</p>
<table>
<thead><tr>
  <th>نمبر</th><th>تاریخ</th><th>خام بکری</th><th>پختہ بکری</th><th>سیل</th>
  <th>وصولی</th><th>لا گا</th><th>کمیشن</th><th>مشیانہ</th><th>مارکیٹ فیس</th><th>اخراجات</th>
</tr></thead>
<tbody>
${rows.map(r => `<tr>
  <td class="left">${r.sr}</td>
  <td class="left">${r.date}</td>
  <td>${fmt(r.khabBikri)}</td>
  <td class="orange">${fmt(r.pukhtaBikri)}</td>
  <td>${fmt(r.sales)}</td>
  <td>${fmt(r.recoveries)}</td>
  <td>${fmt(r.lagan)}</td>
  <td>${fmt(r.commission)}</td>
  <td>${fmt(r.mashayana)}</td>
  <td>${fmt(r.marketFee)}</td>
  <td>${fmt(r.ikhrajat)}</td>
</tr>`).join('')}
</tbody>
<tfoot><tr class="total-row">
  <td colspan="2" class="left">Total</td>
  <td>${fmt(totals.khabBikri)}</td>
  <td>${fmt(totals.pukhtaBikri)}</td>
  <td>${fmt(totals.sales)}</td>
  <td>${fmt(totals.recoveries)}</td>
  <td>${fmt(totals.lagan)}</td>
  <td>${fmt(totals.commission)}</td>
  <td>${fmt(totals.mashayana)}</td>
  <td>${fmt(totals.marketFee)}</td>
  <td>${fmt(totals.ikhrajat)}</td>
</tr></tfoot>
</table>
<p style="text-align:center;margin-top:12px;font-size:10px;color:#888;">روزنامچہ &nbsp;&nbsp;&nbsp; صفحہ نمبر ۱</p>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const HEADER_BG = '#7a9e7e'; // olive green like image

  return (
    <>
      <Helmet>
        <title>روزنامچہ</title>
      </Helmet>


      <div style={{ padding: '12px 16px', fontFamily: 'Arial, sans-serif', fontSize: '13px', background: '#f8f8f8', minHeight: '100vh' }}>

        {/* ── Page Title ── */}
        <div style={{ background: HEADER_BG, color: '#fff', textAlign: 'center', padding: '8px', fontWeight: 'bold', fontSize: '15px', borderRadius: '4px 4px 0 0', marginBottom: '0', ...UR }}>
          روزنامچہ (Day Book)
        </div>

        {/* ── Filter Bar ── */}
        <div style={{ background: '#fff', border: '1px solid #ccc', borderTop: 'none', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>

          {/* Monthly Selection */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={selMonth}
              onChange={e => handleMonthYearChange(parseInt(e.target.value), selYear)}
              style={{ padding: '4px 8px', borderRadius: '3px', border: '1px solid #bbb', fontSize: '13px', ...UR }}
            >
              {urduMonths.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <select
              value={selYear}
              onChange={e => handleMonthYearChange(selMonth, parseInt(e.target.value))}
              style={{ padding: '4px 8px', borderRadius: '3px', border: '1px solid #bbb', fontSize: '13px' }}
            >
              {[...Array(5)].map((_, i) => {
                const y = new Date().getFullYear() - 2 + i;
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
          </div>

          <label
            onClick={() => { try { dateFromRef.current?.showPicker(); } catch (err) { dateFromRef.current?.focus(); } }}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', fontSize: '12px', cursor: 'pointer', ...UR }}
          >
            <span>سے:</span>
            <input
              type="date"
              ref={dateFromRef}
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              onClick={(e) => { e.stopPropagation(); try { e.target.showPicker && e.target.showPicker(); } catch (err) { } }}
              onFocus={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch (err) { } }}
              style={{ border: '1px solid #bbb', borderRadius: '3px', padding: '3px 6px', fontSize: '12px', cursor: 'pointer', outline: 'none' }}
            />
          </label>
          <label
            onClick={() => { try { dateToRef.current?.showPicker(); } catch (err) { dateToRef.current?.focus(); } }}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', fontSize: '12px', cursor: 'pointer', ...UR }}
          >
            <span>تک:</span>
            <input
              type="date"
              ref={dateToRef}
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              onClick={(e) => { e.stopPropagation(); try { e.target.showPicker && e.target.showPicker(); } catch (err) { } }}
              onFocus={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch (err) { } }}
              style={{ border: '1px solid #bbb', borderRadius: '3px', padding: '3px 6px', fontSize: '12px', cursor: 'pointer', outline: 'none' }}
            />
          </label>
          <button onClick={fetchAll} disabled={loading}
            style={{ background: HEADER_BG, color: '#fff', border: 'none', borderRadius: '3px', padding: '4px 14px', cursor: 'pointer', fontWeight: '600', fontSize: '12px', ...UR }}>
            {loading ? 'لوڈ ہو رہا ہے…' : 'تازہ کریں'}
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handlePrint}
              style={{ background: '#4a6fa5', color: '#fff', border: 'none', borderRadius: '3px', padding: '4px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', ...UR }}>
              🖨 پرنٹ
            </button>
            <button onClick={handleExcel}
              style={{ background: '#217346', color: '#fff', border: 'none', borderRadius: '3px', padding: '4px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', ...UR }}>
              📥 ایکسل
            </button>
          </div>
        </div>

        {/* ── Table ── */}
        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #bbb' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto', fontSize: '12px' }}>
            <thead>
              <tr>
                {['نمبر', 'تاریخ', 'خام بکری', 'پختہ بکری', 'سیل', 'وصولی', 'لا گا', 'کمیشن', 'مشیانہ', 'مارکیٹ فیس', 'اخراجات']
                  .map((h, i) => (
                    <th key={i} style={{
                      background: '#fff',
                      borderBottom: '2px solid #888',
                      borderRight: '1px solid #ccc',
                      padding: '5px 7px',
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '11.5px',
                      whiteSpace: 'nowrap',
                      color: '#222',
                      ...(i <= 1 ? { textAlign: 'right' } : {}),
                      ...UR
                    }}>{h}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: '30px', color: '#888', ...UR }}>لوڈ ہو رہا ہے…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: '30px', color: '#888', ...UR }}>منتخب تاریخ کے لیے کوئی ڈیٹا نہیں</td></tr>
              ) : rows.map((group, groupIdx) => {
                const isExpanded = expandedDates[group.date];
                return (
                  <React.Fragment key={groupIdx}>
                    {/* Summary Row */}
                    <tr style={{ background: groupIdx % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #e8e8e8', fontWeight: 'bold' }}>
                      <td style={{ padding: '6px 7px', borderRight: '1px solid #e0e0e0', textAlign: 'left', color: '#333' }}>{group.sr}</td>
                      <td
                        onClick={() => setExpandedDates(prev => ({ ...prev, [group.date]: !prev[group.date] }))}
                        style={{ padding: '6px 7px', borderRight: '1px solid #e0e0e0', textAlign: 'left', whiteSpace: 'nowrap', color: '#2563eb', cursor: 'pointer', ...UR }}
                      >
                        <span style={{ fontSize: '10px', marginLeft: '5px' }}>{isExpanded ? '▼' : '▶'}</span>
                        {group.date}
                      </td>
                      <td style={{ padding: '6px 7px', borderRight: '1px solid #e0e0e0', textAlign: 'right', color: '#333' }}>{fmt(group.totals.khabBikri)}</td>
                      <td
                        onClick={() => group.totals.pukhtaBikri > 0 && navigate(`/company/dashboard-part2?tab=bills&date=${group.date}`)}
                        style={{ padding: '6px 7px', borderRight: '1px solid #e0e0e0', textAlign: 'right', color: group.totals.pukhtaBikri > 0 ? '#c75000' : '#333', cursor: group.totals.pukhtaBikri > 0 ? 'pointer' : 'default', textDecoration: group.totals.pukhtaBikri > 0 ? 'underline' : 'none' }}
                      >
                        {fmt(group.totals.pukhtaBikri)}
                      </td>
                      <td
                        onClick={() => group.totals.sales > 0 && navigate(`/company/dashboard-part2?tab=sales&date=${group.date}`)}
                        style={{ padding: '6px 7px', borderRight: '1px solid #e0e0e0', textAlign: 'right', color: group.totals.sales > 0 ? '#2563eb' : '#333', cursor: group.totals.sales > 0 ? 'pointer' : 'default', textDecoration: group.totals.sales > 0 ? 'underline' : 'none' }}
                      >
                        {fmt(group.totals.sales)}
                      </td>
                      <td
                        onClick={() => group.totals.recoveries > 0 && navigate(`/recovery/create?date=${group.date}`)}
                        style={{ padding: '6px 7px', borderRight: '1px solid #e0e0e0', textAlign: 'right', color: group.totals.recoveries > 0 ? '#059669' : '#333', cursor: group.totals.recoveries > 0 ? 'pointer' : 'default', textDecoration: group.totals.recoveries > 0 ? 'underline' : 'none' }}
                      >
                        {fmt(group.totals.recoveries)}
                      </td>
                      <td style={{ padding: '6px 7px', borderRight: '1px solid #e0e0e0', textAlign: 'right', color: '#333' }}>{fmt(group.totals.lagan)}</td>
                      <td style={{ padding: '6px 7px', borderRight: '1px solid #e0e0e0', textAlign: 'right', color: '#333' }}>{fmt(group.totals.commission)}</td>
                      <td style={{ padding: '6px 7px', borderRight: '1px solid #e0e0e0', textAlign: 'right', color: '#333' }}>{fmt(group.totals.mashayana)}</td>
                      <td style={{ padding: '6px 7px', borderRight: '1px solid #e0e0e0', textAlign: 'right', color: '#333' }}>{fmt(group.totals.marketFee)}</td>
                      <td style={{ padding: '6px 7px', textAlign: 'right', color: '#333' }}>{fmt(group.totals.ikhrajat)}</td>
                    </tr>

                    {/* Expandable Detail Section */}
                    {isExpanded && (
                      <tr style={{ background: '#f8fafc' }}>
                        <td colSpan={11} style={{ padding: '0' }}>
                          <div style={{ padding: '15px' }}>
                            <div style={{ background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead>
                                  <tr style={{ background: '#64748b', color: '#fff' }}>
                                    {['نمبر', 'تاریخ', 'خام بکری', 'پختہ بکری', 'سیل', 'وصولی', 'لاگا', 'کمیشن', 'مشیانہ', 'مارکیٹ فیس', 'اخراجات'].map((h, hi) => (
                                      <th key={hi} style={{ padding: '5px 8px', borderRight: '1px solid #475569', textAlign: hi >= 2 ? 'right' : 'left', ...UR }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const maxSubRows = Math.max(group.bills.length, group.sales.length, group.recoveries.length, group.expenses.length);
                                    if (maxSubRows === 0) return <tr><td colSpan={11} style={{ textAlign: 'center', padding: '10px' }}>کوئی تفصیل نہیں</td></tr>;

                                    return Array.from({ length: maxSubRows }).map((_, si) => {
                                      const b = group.bills[si] || {};
                                      const s = group.sales[si] || {};
                                      const r = group.recoveries[si] || {};
                                      const e = group.expenses[si] || {};

                                      return (
                                        <tr key={si} style={{ borderBottom: '1px solid #f1f5f9', background: si % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                          <td style={{ padding: '4px 8px', borderRight: '1px solid #e2e8f0' }}>{si + 1}</td>
                                          <td style={{ padding: '4px 8px', borderRight: '1px solid #e2e8f0' }}>{group.date}</td>
                                          <td style={{ padding: '4px 8px', borderRight: '1px solid #e2e8f0', textAlign: 'right' }}>{fmt(b.khab || 0)}</td>
                                          <td
                                            onClick={() => b.pukhta > 0 && navigate(`/company/dashboard-part2?tab=bills&date=${group.date}`)}
                                            style={{ padding: '4px 8px', borderRight: '1px solid #e2e8f0', textAlign: 'right', color: '#c75000', cursor: b.pukhta > 0 ? 'pointer' : 'default', textDecoration: b.pukhta > 0 ? 'underline' : 'none' }}
                                          >
                                            {fmt(b.pukhta || 0)}
                                          </td>
                                          <td
                                            onClick={() => s.amount > 0 && navigate(`/company/dashboard-part2?tab=sales&date=${group.date}`)}
                                            style={{ padding: '4px 8px', borderRight: '1px solid #e2e8f0', textAlign: 'right', color: '#2563eb', cursor: s.amount > 0 ? 'pointer' : 'default', textDecoration: s.amount > 0 ? 'underline' : 'none' }}
                                          >
                                            {fmt(s.amount || 0)}
                                          </td>
                                          <td style={{ padding: '4px 8px', borderRight: '1px solid #e2e8f0', textAlign: 'right', color: '#059669' }}>{fmt(r.amount || 0)}</td>
                                          <td style={{ padding: '4px 8px', borderRight: '1px solid #e2e8f0', textAlign: 'right' }}>{fmt(b.lagan || 0)}</td>
                                          <td style={{ padding: '4px 8px', borderRight: '1px solid #e2e8f0', textAlign: 'right' }}>{fmt(b.comm || 0)}</td>
                                          <td style={{ padding: '4px 8px', borderRight: '1px solid #e2e8f0', textAlign: 'right' }}>{fmt(b.mash || 0)}</td>
                                          <td style={{ padding: '4px 8px', borderRight: '1px solid #e2e8f0', textAlign: 'right' }}>{fmt(b.mFee || 0)}</td>
                                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmt((b.ikh || 0) + (e.amount || 0))}</td>
                                        </tr>
                                      );
                                    });
                                  })()}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Empty filler rows to match image style (at least 14 rows visible) */}
              {rows.length < 10 && !loading && [...Array(Math.max(0, 10 - rows.length))].map((_, i) => (
                <tr key={`empty-${i}`} style={{ borderBottom: '1px solid #e8e8e8' }}>
                  {[...Array(11)].map((__, j) => (
                    <td key={j} style={{ padding: '3px 7px', borderRight: '1px solid #e0e0e0', height: '22px' }}>&nbsp;</td>
                  ))}
                </tr>
              ))}
            </tbody>

            {/* ── Totals Row ── */}
            <tfoot>
              <tr style={{ background: '#f0f0f0', borderTop: '2px solid #888' }}>
                <td colSpan={2} style={{ padding: '5px 7px', fontWeight: 'bold', borderRight: '1px solid #ccc', textAlign: 'right', ...UR }}>کل</td>
                <td style={{ padding: '5px 7px', textAlign: 'right', fontWeight: 'bold', borderRight: '1px solid #ccc' }}>{fmt(totals.khabBikri)}</td>
                <td style={{ padding: '5px 7px', textAlign: 'right', fontWeight: 'bold', borderRight: '1px solid #ccc', color: '#c75000' }}>{fmt(totals.pukhtaBikri)}</td>
                <td style={{ padding: '5px 7px', textAlign: 'right', fontWeight: 'bold', borderRight: '1px solid #ccc' }}>{fmt(totals.sales)}</td>
                <td style={{ padding: '5px 7px', textAlign: 'right', fontWeight: 'bold', borderRight: '1px solid #ccc' }}>{fmt(totals.recoveries)}</td>
                <td style={{ padding: '5px 7px', textAlign: 'right', fontWeight: 'bold', borderRight: '1px solid #ccc' }}>{fmt(totals.lagan)}</td>
                <td style={{ padding: '5px 7px', textAlign: 'right', fontWeight: 'bold', borderRight: '1px solid #ccc' }}>{fmt(totals.commission)}</td>
                <td style={{ padding: '5px 7px', textAlign: 'right', fontWeight: 'bold', borderRight: '1px solid #ccc' }}>{fmt(totals.mashayana)}</td>
                <td style={{ padding: '5px 7px', textAlign: 'right', fontWeight: 'bold', borderRight: '1px solid #ccc' }}>{fmt(totals.marketFee)}</td>
                <td style={{ padding: '5px 7px', textAlign: 'right', fontWeight: 'bold' }}>{fmt(totals.ikhrajat)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* ── Footer Bar ── */}
        <div style={{ background: HEADER_BG, color: '#fff', textAlign: 'center', padding: '5px', fontSize: '12px', marginTop: '0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: '12px', paddingRight: '12px' }}>
          <span style={UR}>روزنامچہ</span>
          <span style={UR}>{rows.length} ریکارڈ</span>
          <span style={{ cursor: 'pointer', textDecoration: 'underline', ...UR }} onClick={handlePrint}>بڑا کریں ▲</span>
        </div>

      </div>
    </>
  );
}

export default RozNamachaPart2;
